<app-page-shell variant="default">
  <div class="environment-info-page">
    <div class="environment-info-header">
      <div>
        <p class="environment-info-eyebrow">{{ eyebrow }}</p>
        <h1>Environment Information</h1>
        <p class="environment-info-intro">
          Review these restrictions and operating notes before running actions in this environment.
        </p>
      </div>
      <span *ngIf="info" class="environment-info-label">{{ info.environment.label }}</span>
    </div>

    <div *ngIf="loading" class="environment-info-state" role="status">
      <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
      Loading environment information...
    </div>

    <div *ngIf="error && !loading" class="environment-info-state environment-info-state--error" role="alert">
      <span>{{ error }}</span>
      <button type="button" class="btn btn-outline-danger btn-sm" (click)="load()">Retry</button>
    </div>

    <ng-container *ngIf="info && !loading">
      <section aria-labelledby="environment-restrictions-title">
        <h2 id="environment-restrictions-title">Server restrictions</h2>
        <div class="environment-info-grid">
          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Email delivery</h3>
              <span class="environment-badge" [ngClass]="'environment-badge--' + info.restrictions.emailDelivery.mode">
                {{ modeLabel(info.restrictions.emailDelivery.mode) }}
              </span>
            </div>
            <p *ngIf="info.restrictions.emailDelivery.mode === 'disabled'">
              Outbound email delivery is disabled. Campaign and system emails will not be sent.
            </p>
            <p *ngIf="info.restrictions.emailDelivery.mode === 'restricted'">
              Only recipients matching the following patterns can receive email. Other recipients are skipped.
            </p>
            <p *ngIf="info.restrictions.emailDelivery.mode === 'unrestricted'">
              Outbound email is enabled without a recipient whitelist. Confirm recipients before sending.
            </p>
            <div *ngIf="info.restrictions.emailDelivery.allowedRecipients.length" class="environment-chip-list">
              <span
                *ngFor="let recipient of info.restrictions.emailDelivery.allowedRecipients"
                class="environment-chip">
                {{ recipient }}
              </span>
            </div>
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Registration platform push</h3>
              <span
                class="environment-badge"
                [ngClass]="'environment-badge--' + info.restrictions.registrationPlatformPush.mode">
                {{ modeLabel(info.restrictions.registrationPlatformPush.mode) }}
              </span>
            </div>
            <p *ngIf="info.restrictions.registrationPlatformPush.mode === 'restricted'">
              Share-link push is allowed only for the listed project IDs. Other project pushes are skipped and logged.
            </p>
            <p *ngIf="info.restrictions.registrationPlatformPush.mode === 'unrestricted'">
              No project whitelist is configured. Sync may push share links to connected external platforms.
            </p>
            <div
              *ngIf="info.restrictions.registrationPlatformPush.allowedProjectIds.length"
              class="environment-chip-list">
              <span
                *ngFor="let projectId of info.restrictions.registrationPlatformPush.allowedProjectIds"
                class="environment-chip">
                Project {{ projectId }}
              </span>
            </div>
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Campaign sender domains</h3>
              <span
                class="environment-badge"
                [ngClass]="'environment-badge--' + info.restrictions.campaignSenderDomains.mode">
                {{ modeLabel(info.restrictions.campaignSenderDomains.mode) }}
              </span>
            </div>
            <p *ngIf="info.restrictions.campaignSenderDomains.mode === 'restricted'">
              Users without the restricted-domain permission cannot use these domains for From or Reply-To addresses.
            </p>
            <p *ngIf="info.restrictions.campaignSenderDomains.mode === 'unrestricted'">
              No restricted campaign sender domains are configured.
            </p>
            <div
              *ngIf="info.restrictions.campaignSenderDomains.restrictedDomains.length"
              class="environment-chip-list">
              <span
                *ngFor="let domain of info.restrictions.campaignSenderDomains.restrictedDomains"
                class="environment-chip">
                {{ domain }}
              </span>
            </div>
          </article>

          <article class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>Domain verification</h3>
              <span
                class="environment-badge"
                [ngClass]="'environment-badge--' + info.restrictions.domainVerification.mode">
                {{ modeLabel(info.restrictions.domainVerification.mode) }}
              </span>
            </div>
            <p *ngIf="info.restrictions.domainVerification.allowedDomains.length">
              Only the listed sender domains are verified. Verifying any other domain is skipped and changes nothing, and its relay and provider settings cannot be switched.
            </p>
            <p *ngIf="!info.restrictions.domainVerification.allowedDomains.length">
              Every sender domain can be verified.
            </p>
            <div
              *ngIf="info.restrictions.domainVerification.allowedDomains.length"
              class="environment-chip-list">
              <span
                *ngFor="let domain of info.restrictions.domainVerification.allowedDomains"
                class="environment-chip">
                {{ domain }}
              </span>
            </div>
          </article>

          <article
            *ngFor="let restriction of info.restrictions.additional; trackBy: trackRestriction"
            class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>{{ restriction.label }}</h3>
              <span
                class="environment-badge"
                [ngClass]="'environment-badge--' + restriction.status">
                {{ infoStatusLabel(restriction.status) }}
              </span>
            </div>
            <p>{{ restriction.description }}</p>
            <div *ngIf="restriction.values.length" class="environment-chip-list">
              <span *ngFor="let value of restriction.values" class="environment-chip">
                {{ value }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <section *ngIf="info.workflows.length" aria-labelledby="environment-workflows-title">
        <h2 id="environment-workflows-title">Workflow guidance</h2>
        <div class="environment-info-grid">
          <article *ngFor="let workflow of info.workflows; trackBy: trackWorkflow" class="environment-info-card">
            <div class="environment-info-card__heading">
              <h3>{{ workflow.label }}</h3>
              <span class="environment-badge" [ngClass]="'environment-badge--' + workflow.status">
                {{ statusLabel(workflow.status) }}
              </span>
            </div>
            <p>{{ workflow.description }}</p>
            <div *ngIf="workflow.values.length" class="environment-chip-list">
              <span *ngFor="let value of workflow.values" class="environment-chip">
                {{ value }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <section *ngIf="info.notices.length" aria-labelledby="environment-notices-title">
        <h2 id="environment-notices-title">Additional notices</h2>
        <div class="environment-notices">
          <article
            *ngFor="let notice of info.notices; trackBy: trackNotice"
            class="environment-notice"
            [ngClass]="'environment-notice--' + notice.severity">
            <h3>{{ notice.title }}</h3>
            <p>{{ notice.description }}</p>
            <div *ngIf="notice.values.length" class="environment-chip-list">
              <span *ngFor="let value of notice.values" class="environment-chip">
                {{ value }}
              </span>
            </div>
          </article>
        </div>
      </section>

      <p *ngIf="info.updatedAt" class="environment-info-updated">
        Guidance last updated {{ info.updatedAt | date:'medium' }}
      </p>
    </ng-container>
  </div>
</app-page-shell>
